import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { CheckCircle, Loader2, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking"); // checking, success, failed, expired
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);
  
  const sessionId = searchParams.get("session_id");
  const maxAttempts = 10;
  const pollInterval = 2000; // 2 seconds

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    
    checkPaymentStatus();
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/billing/checkout/status/${sessionId}`,
        { withCredentials: true }
      );
      
      const data = response.data;
      
      if (data.payment_status === "paid") {
        setStatus("success");
        setPaymentInfo(data);
        toast.success("¡Pago procesado exitosamente!");
        
        // Redirect to dashboard after 3 seconds
        setTimeout(() => {
          navigate("/dashboard");
        }, 3000);
        return;
      }
      
      if (data.status === "expired") {
        setStatus("expired");
        return;
      }
      
      // Still processing, poll again
      setAttempts(prev => prev + 1);
      setTimeout(checkPaymentStatus, pollInterval);
      
    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatus("failed");
      toast.error("Error al verificar el pago");
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8">
          {status === "checking" && (
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-teal-500/10 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-teal-400 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-3">
                Verificando tu pago...
              </h1>
              <p className="text-slate-400">
                Por favor espera mientras confirmamos tu transacción
              </p>
              <div className="mt-6">
                <div className="flex justify-center gap-2">
                  <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                  <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                  <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-4">
                Intento {attempts + 1} de {maxAttempts}
              </p>
            </div>
          )}

          {status === "success" && (
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-3">
                ¡Pago Exitoso!
              </h1>
              <p className="text-slate-400 mb-6">
                Tu plan ha sido actualizado correctamente
              </p>
              
              {paymentInfo && (
                <div className="bg-slate-800/50 rounded-xl p-4 mb-6 text-left">
                  <h3 className="text-sm font-semibold text-slate-400 mb-2">Detalles del Pago</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Monto:</span>
                      <span className="text-white font-medium">
                        ${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Estado:</span>
                      <span className="text-emerald-400 font-medium">Pagado</span>
                    </div>
                  </div>
                </div>
              )}
              
              <p className="text-sm text-slate-500 mb-4">
                Serás redirigido al dashboard en unos segundos...
              </p>
              
              <Button
                onClick={() => navigate("/dashboard")}
                className="bg-teal-600 hover:bg-teal-500 text-white"
              >
                Ir al Dashboard
              </Button>
            </div>
          )}

          {status === "failed" && (
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-3">
                Error en el Pago
              </h1>
              <p className="text-slate-400 mb-6">
                No pudimos procesar tu pago. Por favor intenta nuevamente.
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={() => navigate("/organization-settings")}
                  variant="outline"
                  className="border-slate-600 text-slate-300"
                >
                  Volver a Configuración
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="bg-teal-600 hover:bg-teal-500 text-white"
                >
                  Ir al Dashboard
                </Button>
              </div>
            </div>
          )}

          {status === "expired" && (
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="w-10 h-10 text-amber-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-3">
                Sesión Expirada
              </h1>
              <p className="text-slate-400 mb-6">
                La sesión de pago ha expirado. Por favor intenta nuevamente.
              </p>
              <Button
                onClick={() => navigate("/organization-settings")}
                className="bg-teal-600 hover:bg-teal-500 text-white"
              >
                Intentar Nuevamente
              </Button>
            </div>
          )}

          {status === "timeout" && (
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="w-10 h-10 text-amber-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-3">
                Verificación Timeout
              </h1>
              <p className="text-slate-400 mb-6">
                No pudimos verificar el estado del pago en este momento. 
                Por favor verifica tu correo para la confirmación o contacta a soporte.
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={() => checkPaymentStatus()}
                  variant="outline"
                  className="border-slate-600 text-slate-300"
                >
                  Reintentar Verificación
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="bg-teal-600 hover:bg-teal-500 text-white"
                >
                  Ir al Dashboard
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
